import React from 'react';
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, BookOpen } from "lucide-react";
import skyLoader from "../../assets/home-assets/skyLoader.gif";

// Modify the AnimatedText component to only handle a single word
const AnimatedWord = ({ word }) => {
  // Define gradient colors for each letter
  const gradients = [
    'bg-gradient-to-r from-blue-400 to-blue-600',
    'bg-gradient-to-r from-blue-500 to-indigo-500',
    'bg-gradient-to-r from-indigo-400 to-blue-400',
    'bg-gradient-to-r from-blue-600 to-sky-500',
    'bg-gradient-to-r from-sky-400 to-blue-500',
    'bg-gradient-to-r from-blue-500 to-cyan-500',
    'bg-gradient-to-r from-cyan-400 to-blue-400',
    'bg-gradient-to-r from-blue-600 to-indigo-400',
    'bg-gradient-to-r from-indigo-500 to-blue-500',
    'bg-gradient-to-r from-blue-400 to-sky-400',
    'bg-gradient-to-r from-sky-500 to-blue-600',
    'bg-gradient-to-r from-blue-500 to-cyan-400',
  ];

  return (
    <span className="inline-block">
      {word.split('').map((letter, index) => (
        <motion.span
          key={index}
          className={`inline-block bg-clip-text text-transparent ${gradients[index % gradients.length]}`}
          initial={{ opacity: 0, y: 20, scale: 0.5 }}
          animate={{ 
            opacity: 1, 
            y: [0, -15, 0], // Increased bounce height
            scale: [1, 1.1, 1], // Added scale animation
          }}
          transition={{
            duration: 0.5,
            delay: index * 0.15, // Slightly increased delay between letters
            y: {
              duration: 1.2, // Slightly slower bounce
              repeat: Infinity,
              repeatDelay: word.length * 0.15, // Adjusted to match new delay
              ease: "easeInOut"
            },
            scale: {
              duration: 1.2,
              repeat: Infinity,
              repeatDelay: word.length * 0.15,
              ease: "easeInOut"
            },
            opacity: {
              duration: 0.5,
              delay: index * 0.15
            }
          }}
          whileHover={{ 
            scale: 1.3,
            transition: { duration: 0.2 }
          }}
          style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {letter}
        </motion.span>
      ))}
    </span>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();

  const handleInnovation = () => {
    navigate("/projects");
  };

  const handleDocs = () => {
    navigate("/docs");
  };

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Background GIF */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <img 
          src={skyLoader} 
          alt="Background animation" 
          className="w-full h-full object-cover brightness-125 contrast-110"
        />
        {/* Very light overlay for minimal text protection */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/20 to-white/10"></div>
        {/* Subtle accent gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-purple-500/5"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-20 md:py-32 flex flex-col justify-center items-center text-center">
        {/* Badge */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block bg-white/40 backdrop-blur-[2px] border border-white/30 text-sm text-indigo-800 px-4 py-2 rounded-full font-medium shadow-lg">
            <span>Decentralized Innovation Platform</span>
          </span>
        </motion.div>

        {/* Heading */}
        <div className="text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 tracking-wide text-gray-900">
            Trustless Open Innovation <br />
            <AnimatedWord word="Infrastructure" />
          </h1>
        </div>

        {/* Description */}
        <motion.p 
          className="text-gray-800 max-w-2xl text-lg md:text-xl mb-10 font-medium"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Building the next generation of scalable and sustainable open innovation ecosystem which is decentralized, incentive-aligned, and truly open.
        </motion.p>

        {/* Buttons */}
        <motion.div 
          className="flex flex-wrap justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <button 
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl hover:translate-y-[-2px] transition-all duration-300 flex items-center"
            onClick={handleInnovation}
          >
            <Sparkles size={18} className="mr-2" />
            Explore Innovations
          </button>
          <button 
            className="bg-white/40 backdrop-blur-[2px] text-indigo-800 font-semibold py-3 px-8 rounded-xl shadow-lg border border-white/30 hover:border-white/50 hover:shadow-xl hover:translate-y-[-2px] transition-all duration-300 flex items-center" 
            onClick={handleDocs}
          >
            <BookOpen size={18} className="mr-2" />
            Learn More
          </button>
        </motion.div>
      </div>
    </section>
  );
}

export default HeroSection;
